import request from '../../utils/request';
import Logger from '../../utils/logger';
import { parseBlock } from './vtt-parser';
import { parseHlsAttributes } from './utils';

export default class SafariSubtitlesManager {
  constructor(player) {
    this.player = player;
  }

  _textTracks = [];
  _manifestSubtitles = [];
  _loadedSegments = [];
  _loadingSubtitlesManifest = false;

  async _loadSegmentCues(url) {
    const segmentFile = await request({
      url,
      type: 'GET',
      authorize: false,
    });

    const blocks = segmentFile.split(/(\r?\n){2,}/gm);

    return blocks.slice(1)
      .map(item => parseBlock(item))
      .filter(Boolean);
  }

  async _loadManifestSubtitles() {
    if (this._loadingSubtitlesManifest) return;

    this._loadingSubtitlesManifest = true;

    const getAbsoluteUrl = relative => (
      this.player.parent.model.playlist
        .split('/')
        .slice(0, -1)
        .concat([relative])
        .join('/')
    );

    let pl;

    try {
      pl = await request({
        url: this.player.parent.model.playlist,
        type: 'GET',
        authorize: false,
      });
    } catch (e) {
      this._loadingSubtitlesManifest = false;
      Logger.error('SafariSubtitlesManager: failed to load playlist', e);
      return;
    }

    const manifestSubtitles = pl.split('\n')
      .filter(str => str.startsWith('#EXT-X-MEDIA') && str.includes('TYPE=SUBTITLES'))
      .map((str) => {
        const attributes = parseHlsAttributes(str);
        return ({
          language: attributes.LANGUAGE,
          label: attributes.NAME,
          url: getAbsoluteUrl(attributes.URI),
        });
      });

    await Promise.all(manifestSubtitles.map(async (manifestSubtitle) => {
      let subManifest;

      try {
        subManifest = await request({
          url: manifestSubtitle.url,
          type: 'GET',
          authorize: false,
        });
      } catch (e) {
        Logger.error('SafariSubtitlesManager: failed to load subtitles', e);
        return;
      }

      let startTime = 0;

      const lines = subManifest.split('\n');
      const segments = lines.reduce((acc, str, idx) => {
        if (str.startsWith('#EXTINF')) {
          const duration = Number(str.replace(',', '').split(':')[1]);
          let url = lines[idx + 1];
          if (!url.startsWith('http')) {
            url = getAbsoluteUrl(lines[idx + 1]);
          }
          acc.push({
            duration,
            startTime,
            url,
          });
          startTime += duration;
        }
        return acc;
      }, []);

      this._manifestSubtitles.push({
        ...manifestSubtitle,
        segments,
      });
    }));

    this._loadingSubtitlesManifest = false;
  }

  _loadTextTracks = async () => {
    const currentTime = this.player.playerTimeToAbsoluteTime(this.player.parent.playerElement.currentTime);
    const PRELOAD_DURATION = 60; // preload text track for 1 min in the future.

    const activeTrack = Array.from(this.player.parent.playerElement.textTracks)
      .find(tt => tt.mode === 'showing');

    if (!activeTrack) {
      return;
    }

    const manifest = this._manifestSubtitles
      .find(man => man.label === activeTrack.label);

    if (!manifest) {
      return;
    }

    const futureSegments = manifest.segments
      .filter(segment => (
        segment.startTime + segment.duration >= currentTime
        && segment.startTime + segment.duration <= currentTime + PRELOAD_DURATION
        && !this._loadedSegments.includes(segment.url)
      ));

    await Promise.all(futureSegments.map(async (segment) => {
      this._loadedSegments.push(segment.url);

      try {
        const cues = await this._loadSegmentCues(segment.url);

        cues
          .filter(shakaCue => !activeTrack.cues?.getCueById(`${shakaCue.startTime}-${shakaCue.endTime}`))
          .forEach((shakaCue) => {
            const vttCue = new VTTCue(
              shakaCue.startTime,
              shakaCue.endTime,
              shakaCue.payload,
            );
            vttCue.id = `${shakaCue.startTime}-${shakaCue.endTime}`;
            vttCue.line = this.player.parent.model.textTracksPosition;
            activeTrack.addCue(vttCue);
          });
      } catch (e) {
        Logger.error('SafariSubtitlesManager: failed to load segment cues', e);
      }
    }));
  };

  _disableTrack(track) {
    try {
      track.mode = 'disabled';
      track.kind = 'metadata';
    } catch (e) {
      Logger.error('SafariSubtitlesManager: failed to disable track', e);
    }
  }

  _duplicateTextTracks = (e) => {
    if (this._textTracks.some(tt => tt.label === e.track.label)) return;

    const track = this.player.parent.playerElement
      .addTextTrack(e.track.kind, e.track.label, e.track.language);

    this._disableTrack(e.track);

    this._textTracks.push(track);
  };

  setup() {
    this._clear();

    this.player.parent.playerElement.textTracks.addEventListener('addtrack', this._duplicateTextTracks);
    this.player.parent.playerElement.addEventListener('timeupdate', this._loadTextTracks);
    this._loadManifestSubtitles();
  }

  clearLoadedTextTrackSegments() {
    this._loadedSegments = [];
  }

  unload() {
    this.player.parent.playerElement.removeEventListener('timeupdate', this._loadTextTracks);
    this.player.parent.playerElement.textTracks.removeEventListener('addtrack', this._duplicateTextTracks);

    this._clear();
  }

  _clear() {
    for (const track of this.player.parent.playerElement.textTracks) {
      this._disableTrack(track);
    }

    this._loadedSegments = [];
    this._textTracks = [];
    this._manifestSubtitles = [];
    this._loadingSubtitlesManifest = false;
  }

  destroy() {
    this.unload();
    delete this.player;
  }
}
